/**
 * Minimal fetch wrapper for the calls worker: prepends CALLS_SERVICE and
 * attaches the caller's service-auth JWT as a bearer. Replaces the electron
 * cfcalls `apiFetch` (which pulled its token from a redux provider); here the
 * token comes from a getToken() thunk so it can be re-minted on expiry.
 */

import {CALLS_SERVICE} from '#/lib/constants'

export interface CallFetch {
  (path: string, init?: RequestInit): Promise<Response>
}

/**
 * Builds a fetch bound to a token source. Retries once with a freshly minted
 * token on a 401 (service-auth tokens are short-lived).
 *
 * @param getToken - Returns a valid service-auth JWT.
 */
export function makeCallFetch(getToken: () => Promise<string>): CallFetch {
  return async function callFetch(path: string, init: RequestInit = {}) {
    const url = path.startsWith('http') ? path : `${CALLS_SERVICE}${path}`

    const doFetch = async (token: string) => {
      const headers = new Headers(init.headers || {})
      headers.set('Authorization', `Bearer ${token}`)
      return fetch(url, {...init, headers})
    }

    let res = await doFetch(await getToken())
    if (res.status === 401) {
      // Token likely expired mid-session -- re-mint once and retry.
      res = await doFetch(await getToken())
    }
    return res
  }
}

/**
 * Convenience: callFetch + JSON parse, throwing on non-2xx.
 */
export async function callFetchJson<T = unknown>(
  callFetch: CallFetch,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await callFetch(path, init)
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`calls ${path} failed: ${res.status} ${text.slice(0, 200)}`)
  }
  return res.json() as Promise<T>
}
